import type { SupabaseClient, User } from '@supabase/supabase-js';
import type { AccountStatus, UserProfile, UserRole } from '@/types/user';
import { normalizeAccountStatus } from '@/lib/auth/accountStatus';

/** Fields needed by middleware for route protection. */
export interface OwnProfileSummary {
  role: UserRole;
  account_status: AccountStatus;
  store_id: string | null;
}

async function fetchOwnProfileViaRpcs(
  supabase: SupabaseClient,
  authUser: User
): Promise<UserProfile | null> {
  const [roleRes, statusRes, storeRes] = await Promise.all([
    supabase.rpc('get_my_role'),
    supabase.rpc('get_my_account_status'),
    supabase.rpc('get_my_store_id'),
  ]);

  if (roleRes.error || statusRes.error || !roleRes.data) {
    return null;
  }

  const status = normalizeAccountStatus(statusRes.data);
  if (!status) return null;

  return {
    id: authUser.id,
    email: authUser.email ?? '',
    full_name: (authUser.user_metadata?.full_name as string | undefined) ?? null,
    role: roleRes.data as UserRole,
    account_status: status as AccountStatus,
    phone: (authUser.user_metadata?.phone as string | undefined) ?? null,
    store_id: storeRes.data ?? null,
    created_at: authUser.created_at ?? '',
    updated_at: authUser.updated_at ?? authUser.created_at ?? '',
  };
}

/**
 * Load the authenticated user's profile without querying user_profiles directly.
 * Uses SECURITY DEFINER RPCs so RLS recursion (42P17) cannot block login/portal load.
 */
export async function fetchOwnProfile(
  supabase: SupabaseClient,
  authUser: User
): Promise<UserProfile | null> {
  const { data, error } = await supabase.rpc('get_my_profile');

  if (!error && data) {
    const row = (Array.isArray(data) ? data[0] : data) as UserProfile | undefined;
    const status = normalizeAccountStatus(row?.account_status);
    // Require a real account_status — partial rows must not be treated as inactive.
    if (row?.id && status) {
      return {
        ...row,
        account_status: status as AccountStatus,
      };
    }
  }

  return fetchOwnProfileViaRpcs(supabase, authUser);
}

export async function fetchOwnProfileSummary(
  supabase: SupabaseClient,
  authUser: User
): Promise<OwnProfileSummary | null> {
  const profile = await fetchOwnProfile(supabase, authUser);
  if (!profile?.account_status) return null;

  return {
    role: profile.role,
    account_status: profile.account_status,
    store_id: profile.store_id,
  };
}
